import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  MapPin,
  Plus,
  Power,
  Radar,
  ShieldAlert,
  X,
} from "lucide-react";
import {
  createGeofence,
  listAllGeofences,
  listGeofenceEvents,
  updateGeofence,
} from "../api";
import type { AuthUser, Geofence, GeofenceEventDetail, GeofenceInput } from "../types";

const initialForm: GeofenceInput = {
  code: "",
  name: "",
  kind: "risk",
  severity: "warning",
  center_latitude: 28.633,
  center_longitude: -106.0691,
  radius_m: 1500,
  active: true,
  metadata_json: {},
};

export function GeofencePanel({
  open,
  onClose,
  user,
  onGeofencesChange,
}: {
  open: boolean;
  onClose: () => void;
  user: AuthUser;
  onGeofencesChange: () => void;
}) {
  const [rows, setRows] = useState<Geofence[]>([]);
  const [events, setEvents] = useState<GeofenceEventDetail[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<GeofenceInput>(initialForm);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const canManage = ["superadmin", "fleet_admin"].includes(user.role);

  const refresh = () => {
    Promise.all([listAllGeofences(), listGeofenceEvents()])
      .then(([geofences, eventRows]) => {
        setRows(geofences);
        setEvents(eventRows);
        setSelectedId((current) =>
          geofences.some((item) => item.id === current)
            ? current
            : geofences[0]?.id ?? null,
        );
        setError("");
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : "No fue posible cargar geocercas"),
      );
  };

  useEffect(() => {
    if (!open) return;
    refresh();
    window.addEventListener("gsf:geofence-config-updated", refresh);
    window.addEventListener("gsf:geofence-event-updated", refresh);
    return () => {
      window.removeEventListener("gsf:geofence-config-updated", refresh);
      window.removeEventListener("gsf:geofence-event-updated", refresh);
    };
  }, [open]);

  const selected = rows.find((row) => row.id === selectedId) || rows[0];
  const selectedEvents = events.filter((event) => event.geofence_id === selected?.id);
  const summary = useMemo(
    () => ({
      active: rows.filter((row) => row.active).length,
      risk: rows.filter((row) => ["risk", "restricted"].includes(row.kind) && row.active).length,
      transitions: events.length,
    }),
    [events.length, rows],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const created = await createGeofence({
        ...form,
        code: form.code.trim().toUpperCase().replaceAll(" ", "-"),
        name: form.name.trim(),
      });
      setRows((current) => [...current, created]);
      setSelectedId(created.id);
      setCreating(false);
      setForm(initialForm);
      onGeofencesChange();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible crear la geocerca");
    } finally {
      setPending(false);
    }
  }

  async function toggle() {
    if (!selected) return;
    setPending(true);
    try {
      const updated = await updateGeofence(selected.id, { active: !selected.active });
      setRows((current) => current.map((row) => row.id === updated.id ? updated : row));
      onGeofencesChange();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible actualizar la geocerca");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel geofence-panel" role="dialog" aria-modal="true" aria-labelledby="geofence-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">CONTROL GEOESPACIAL · ZONAS CIRCULARES AUDITADAS</span>
            <h2 id="geofence-title">Geocercas operativas</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar geocercas"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Radar size={17} /><b>{summary.active}</b> activas</span>
          <span><ShieldAlert size={17} /><b>{summary.risk}</b> de riesgo</span>
          <span><MapPin size={17} /><b>{summary.transitions}</b> transiciones</span>
        </section>
        <div className="geofence-panel__body">
          <section className="geofence-catalog">
            <div className="admin-section-title">
              <span className="eyebrow">CATÁLOGO NACIONAL</span>
              {canManage && <button type="button" onClick={() => setCreating((value) => !value)}><Plus size={12} /> NUEVA ZONA</button>}
            </div>
            {creating && (
              <form className="geofence-form" onSubmit={submit}>
                <label>CÓDIGO<input required minLength={3} value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} placeholder="RIESGO-CUU-01" /></label>
                <label>NOMBRE<input required minLength={3} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Corredor Chihuahua Norte" /></label>
                <label>TIPO<select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value as GeofenceInput["kind"] })}><option value="risk">Riesgo</option><option value="restricted">Restringida</option><option value="yard">Patio</option><option value="customer">Cliente</option><option value="operational">Operativa</option><option value="hq">Sede</option></select></label>
                <label>SEVERIDAD<select value={form.severity} onChange={(event) => setForm({ ...form, severity: event.target.value as GeofenceInput["severity"] })}><option value="warning">Advertencia</option><option value="critical">Crítica</option><option value="normal">Normal</option></select></label>
                <label>LATITUD<input required type="number" step="0.000001" value={form.center_latitude} onChange={(event) => setForm({ ...form, center_latitude: Number(event.target.value) })} /></label>
                <label>LONGITUD<input required type="number" step="0.000001" value={form.center_longitude} onChange={(event) => setForm({ ...form, center_longitude: Number(event.target.value) })} /></label>
                <label>RADIO METROS<input required type="number" min="50" max="200000" value={form.radius_m} onChange={(event) => setForm({ ...form, radius_m: Number(event.target.value) })} /></label>
                <button type="submit" disabled={pending}><Radar size={13} /> CREAR Y ACTIVAR</button>
              </form>
            )}
            <div className="geofence-list">
              {rows.map((geofence) => (
                <button type="button" className={`geofence-item geofence-item--${geofence.severity}${selected?.id === geofence.id ? " is-selected" : ""}${!geofence.active ? " is-disabled" : ""}`} onClick={() => { setSelectedId(geofence.id); setCreating(false); }} key={geofence.id}>
                  <MapPin size={16} />
                  <span><small>{geofence.code} · {geofence.kind}</small><strong>{geofence.name}</strong><em>{(geofence.radius_m / 1000).toFixed(1)} km de radio</em></span>
                  <b>{geofence.active ? "ACTIVA" : "INACTIVA"}</b>
                </button>
              ))}
            </div>
          </section>
          <section className="geofence-detail">
            {selected && (
              <>
                <div className={`geofence-radar geofence-radar--${selected.severity}`}><Radar size={34} /><i /><i /><i /></div>
                <div className="geofence-detail__title">
                  <span className="eyebrow">{selected.code} · {selected.kind.toUpperCase()}</span>
                  <h3>{selected.name}</h3>
                  <p>{selected.center_latitude.toFixed(5)}, {selected.center_longitude.toFixed(5)}</p>
                </div>
                <div className="geofence-stats">
                  <article><MapPin size={16} /><span><small>RADIO DE CONTROL</small><strong>{selected.radius_m.toLocaleString("es-MX")} m</strong></span></article>
                  <article><AlertTriangle size={16} /><span><small>SEVERIDAD</small><strong>{selected.severity.toUpperCase()}</strong></span></article>
                </div>
                <div className="geofence-events">
                  <span className="eyebrow">TRANSICIONES RECIENTES</span>
                  {selectedEvents.map((event) => (
                    <article key={event.id}>
                      {event.transition === "entered" ? <ArrowDownToLine size={14} /> : <ArrowUpFromLine size={14} />}
                      <span><strong>{event.vehicle_unit_code} · {event.transition === "entered" ? "ENTRADA" : "SALIDA"}</strong><small>{new Date(event.captured_at).toLocaleString("es-MX")}</small></span>
                    </article>
                  ))}
                  {!selectedEvents.length && <p className="maintenance-empty">SIN TRANSICIONES REGISTRADAS</p>}
                </div>
                {canManage && <button type="button" className={`geofence-power ${selected.active ? "is-active" : ""}`} disabled={pending} onClick={toggle}><Power size={14} /> {selected.active ? "DESACTIVAR ZONA" : "ACTIVAR ZONA"}</button>}
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
